import * as THREE from "three";

export interface Candle {
  readonly group: THREE.Group;
  readonly flame: THREE.Mesh;
  readonly light: THREE.PointLight;
}

const BRASS = new THREE.MeshStandardMaterial({ color: "#c9a349", metalness: 0.7, roughness: 0.32 });
const WAX = new THREE.MeshStandardMaterial({ color: "#f3e6c9", roughness: 0.6 });
const WICK = new THREE.MeshStandardMaterial({ color: "#2a1a10" });
const FLAME = new THREE.MeshBasicMaterial({ color: "#ffb347", transparent: true, opacity: 0.95 });

/** Chandelle procedurale (bougeoir en laiton + cire + flamme) : socle, tige, coupelle, cierge, meche, flamme + sa propre lumiere ponctuelle. */
function createCandle(): Candle {
  const group = new THREE.Group();

  const base = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.13, 0.03, 16), BRASS);
  base.position.y = 0.015;
  group.add(base);

  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.03, 0.14, 12), BRASS);
  stem.position.y = 0.03 + 0.07;
  group.add(stem);

  const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.03, 0.04, 14), BRASS);
  const cupY = 0.03 + 0.14 + 0.02;
  cup.position.y = cupY;
  group.add(cup);

  const waxHeight = 0.28;
  const waxBottomY = cupY + 0.02;
  const wax = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.04, waxHeight, 14), WAX);
  wax.position.y = waxBottomY + waxHeight / 2;
  group.add(wax);

  const wick = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.005, 0.035, 6), WICK);
  const wickTopY = waxBottomY + waxHeight + 0.017;
  wick.position.y = wickTopY;
  group.add(wick);

  const flame = new THREE.Mesh(new THREE.SphereGeometry(0.03, 10, 10), FLAME);
  flame.scale.set(0.65, 1.4, 0.65);
  flame.position.y = wickTopY + 0.03;
  group.add(flame);

  const light = new THREE.PointLight("#ff9a3d", 4.8, 11, 1.8);
  light.position.copy(flame.position);
  group.add(light);

  group.traverse((child) => {
    if (child instanceof THREE.Mesh) child.castShadow = true;
  });

  return { group, flame, light };
}

/** Pose des chandelles allumees aux positions donnees (X/Z), socle au niveau `y` (dessus de la table). */
export function placeCandles(
  parent: THREE.Object3D,
  positions: readonly [number, number][],
  y: number,
  scale = 1,
): Candle[] {
  return positions.map(([x, z]) => {
    const candle = createCandle();
    candle.group.position.set(x, y, z);
    candle.group.scale.setScalar(scale);
    parent.add(candle.group);
    return candle;
  });
}
